<script lang="ts">
  import { editorStore } from '../../../stores/editorStore';
  import { undoStore, canUndo } from '../../../stores/undoStore';
  import { announceToScreenReader } from '../../../lib/accessibility';

  let showConfirmDialog = false;

  async function handleSave() {
    showConfirmDialog = true;
  }

  async function confirmSave() {
    showConfirmDialog = false;
    try {
      await editorStore.saveChanges();
      if (!$editorStore.conflicts) {
        if ($editorStore.autoMergedItems.length > 0) {
          const message = `Changes saved successfully! Auto-merged ${$editorStore.autoMergedItems.length} item(s) with another editor's changes.`;
          announceToScreenReader(message, 'polite');
          console.log('[Auto-Merge] Items merged:', $editorStore.autoMergedItems);
        } else {
          announceToScreenReader('All changes saved successfully', 'polite');
        }
      } else {
        announceToScreenReader('Conflicts detected with other editors', 'assertive');
      }
    } catch (error) {
      announceToScreenReader('Error saving changes', 'assertive');
    }
  }

  function cancelSave() {
    showConfirmDialog = false;
  }

  function handleUndo() {
    const lastAction = undoStore.undo();
    if (lastAction) {
      // Restore previous value to editorStore
      const { target, targetKey, previousValue } = lastAction;

      if (target === 'wordbank') {
        editorStore.updateLocalData('wordbanks', targetKey, previousValue);
      } else if (target === 'prompt') {
        editorStore.updateLocalData('prompts', targetKey, previousValue);
      } else if (target === 'framework') {
        editorStore.updateLocalData('frameworks', targetKey, previousValue);
      }

      announceToScreenReader('Action undone', 'polite');
    }
  }

  function useLocalVersion(index: number) {
    editorStore.resolveConflictUseLocal(index);
    announceToScreenReader('Using your version for this item', 'polite');
  }

  function useServerVersion(index: number) {
    editorStore.resolveConflictUseServer(index);
    announceToScreenReader('Using server version for this item', 'polite');
  }

  function retrySave() {
    editorStore.clearConflicts();
    handleSave();
  }

  function closeConflicts() {
    editorStore.clearConflicts();
  }
</script>

<header class="top-bar">
  <div class="top-bar-left">
    <h1>Content Editor</h1>
  </div>

  <div class="top-bar-right">
    {#if $editorStore.hasUnsavedChanges}
      <span class="unsaved-indicator" role="status" aria-live="polite">
        Unsaved changes
      </span>
    {/if}

    <button
      on:click={handleUndo}
      disabled={!$canUndo}
      aria-label="Undo last change"
      class="undo-button"
    >
      Undo
    </button>

    <button
      on:click={handleSave}
      disabled={!$editorStore.hasUnsavedChanges || $editorStore.isSaving}
      aria-label="Save all changes"
      class="save-button"
    >
      {$editorStore.isSaving ? 'Saving...' : 'Save Changes'}
    </button>
  </div>
</header>

{#if showConfirmDialog}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div class="confirm-overlay" role="presentation" on:click={cancelSave} on:keydown={(e) => e.key === 'Escape' && cancelSave()}>
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div class="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-title" on:click|stopPropagation on:keydown|stopPropagation>
      <h2 id="confirm-title">Confirm Save</h2>
      <p>Are you sure you want to save all changes? This will update the YAML files on the server.</p>

      <div class="confirm-actions">
        <button on:click={cancelSave} class="cancel-button">
          Cancel
        </button>
        <button on:click={confirmSave} class="confirm-button">
          Save Changes
        </button>
      </div>
    </div>
  </div>
{/if}

{#if $editorStore.conflicts && $editorStore.conflicts.length > 0}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div class="confirm-overlay" role="presentation" on:click={closeConflicts} on:keydown={(e) => e.key === 'Escape' && closeConflicts()}>
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div class="conflict-dialog" role="dialog" aria-modal="true" aria-labelledby="conflict-title" on:click|stopPropagation on:keydown|stopPropagation>
      <h2 id="conflict-title">Conflicts Detected</h2>
      <p class="conflict-description">
        Another editor has modified the following items since you loaded them.
        Please choose which version to keep for each conflict.
      </p>

      <div class="conflicts-list">
        {#each $editorStore.conflicts as conflict, index}
          <div class="conflict-item">
            <h3>{conflict.type}: {conflict.key}</h3>
            <p class="conflict-info">
              This {conflict.type.slice(0, -1)} was modified by another editor.
            </p>

            <div class="conflict-actions">
              <button
                on:click={() => useServerVersion(index)}
                class="conflict-button server-button"
                aria-label="Use server version for {conflict.key}"
              >
                Use Server Version
              </button>
              <button
                on:click={() => useLocalVersion(index)}
                class="conflict-button local-button"
                aria-label="Keep my version for {conflict.key}"
              >
                Keep My Version
              </button>
            </div>
          </div>
        {/each}
      </div>

      <div class="conflict-footer">
        <p class="conflict-help">
          After resolving all conflicts, click "Retry Save" to save your changes.
        </p>
        <div class="confirm-actions">
          <button on:click={closeConflicts} class="cancel-button">
            Close
          </button>
          <button
            on:click={retrySave}
            class="confirm-button"
            disabled={$editorStore.conflicts && $editorStore.conflicts.length > 0}
          >
            Retry Save
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 2rem;
    background: var(--color-bg-secondary, #f5f5f5);
    border-bottom: 2px solid var(--color-border, #ddd);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .top-bar-left h1 {
    margin: 0;
    font-size: 1.5rem;
    color: var(--color-text-primary, #333);
  }

  .top-bar-right {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .unsaved-indicator {
    padding: 0.25rem 0.75rem;
    background: var(--color-warning-bg, #fff3cd);
    color: var(--color-warning-text, #856404);
    border: 1px solid var(--color-warning-border, #ffc107);
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .undo-button {
    background: var(--color-secondary-bg, #6c757d);
    color: white;
  }

  .undo-button:hover:not(:disabled) {
    background: var(--color-secondary-hover, #5a6268);
  }

  .undo-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .save-button {
    background: var(--color-primary, #007bff);
    color: white;
    font-weight: 600;
  }

  .save-button:hover:not(:disabled) {
    background: var(--color-primary-hover, #0056b3);
  }

  .save-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .confirm-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .confirm-dialog {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .confirm-dialog h2 {
    margin-top: 0;
    color: var(--color-text-primary, #333);
  }

  .confirm-dialog p {
    color: var(--color-text-secondary, #666);
    line-height: 1.5;
  }

  .confirm-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
  }

  .cancel-button {
    background: var(--color-secondary-bg, #6c757d);
    color: white;
  }

  .cancel-button:hover {
    background: var(--color-secondary-hover, #5a6268);
  }

  .confirm-button {
    background: var(--color-primary, #007bff);
    color: white;
    font-weight: 600;
  }

  .confirm-button:hover {
    background: var(--color-primary-hover, #0056b3);
  }

  /* Conflict Dialog Styles */
  .conflict-dialog {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    max-width: 700px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .conflict-dialog h2 {
    margin-top: 0;
    color: var(--color-error, #dc3545);
  }

  .conflict-description {
    color: var(--color-text-secondary, #666);
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  .conflicts-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .conflict-item {
    border: 2px solid var(--color-error, #dc3545);
    border-radius: 4px;
    padding: 1rem;
    background: var(--color-error-bg, #f8d7da);
  }

  .conflict-item h3 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: var(--color-error, #dc3545);
    font-size: 1rem;
  }

  .conflict-info {
    margin: 0.5rem 0;
    color: var(--color-text-secondary, #666);
    font-size: 0.875rem;
  }

  .conflict-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .conflict-button {
    flex: 1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .server-button {
    background: var(--color-secondary-bg, #6c757d);
    color: white;
  }

  .server-button:hover {
    background: var(--color-secondary-hover, #5a6268);
  }

  .local-button {
    background: var(--color-primary, #007bff);
    color: white;
  }

  .local-button:hover {
    background: var(--color-primary-hover, #0056b3);
  }

  .conflict-footer {
    border-top: 1px solid var(--color-border, #ddd);
    padding-top: 1rem;
  }

  .conflict-help {
    color: var(--color-text-secondary, #666);
    font-size: 0.875rem;
    margin: 0 0 1rem 0;
  }
</style>
